// NodeDateTimeRangeField.jsx

import React, { useCallback } from 'react';

// CSS
import './NodeDateTimeRangeField.css';

// Components
import DateTimeRangeRepeatEditor from 'node/form/fields/datetimerange/DateTimeRangeRepeatEditor';
import NodeDateTimeRangeInput from 'node/form/fields/datetimerange/NodeDateTimeRangeInput';
import NodeTemporalReadOnlyValue from 'node/form/fields/utilities/NodeTemporalReadOnlyValue';

/**
 * NodeDateTimeRangeField
 *
 * Displays a schema-driven datetime range field with inline start/end controls
 * and optional weekly recurrence (node value, not schema).
 * In readOnly / previewMode: friendly label (+ recurrence hint); no editors.
 *
 * @param {object} value - Current field value.
 * @param {Function} onChange - Callback when datetime range changes.
 * @param {boolean} [readOnly=false]
 * @param {boolean} [previewMode=false]
 *
 * @returns {ReactNode} The rendered component.
 */
const NodeDateTimeRangeField = ({
  value,
  onChange,
  readOnly = false,
  previewMode = false,
}) => {
  const isReadOnly = Boolean(readOnly || previewMode);

  const handleRangeChange = useCallback((nextValue) => {
    if (typeof onChange !== 'function') return;

    if (!nextValue) {
      onChange(null);
      return;
    }

    if (value?.recurrence) {
      onChange({ ...nextValue, recurrence: value.recurrence });
      return;
    }

    onChange(nextValue);
  }, [onChange, value?.recurrence]);

  const handleRepeatChange = useCallback((nextValue) => {
    if (typeof onChange !== 'function') return;
    onChange(nextValue || null);
  }, [onChange]);

  if (isReadOnly) {
    return (
      <div className="node-date-time-range-field-container is-readonly">
        <NodeTemporalReadOnlyValue value={value} fieldType="datetimerange" />
      </div>
    );
  }

  return (
    <div className="node-date-time-range-field-container">
      <div className="node-date-time-range-field-form">
        <div className="node-date-time-range-field-connector" />
        <div className="node-date-range-field-picker">
          <NodeDateTimeRangeInput
            value={value}
            autoFillEnd
            onChange={handleRangeChange}
          />
        </div>
      </div>

      <div className="node-date-time-range-field-repeat">
        <DateTimeRangeRepeatEditor
          value={value}
          onChange={handleRepeatChange}
        />
      </div>
    </div>
  );
};

NodeDateTimeRangeField.displayName = 'NodeDateTimeRangeField';

export default NodeDateTimeRangeField;
